<template>
  <div class="d-flex flex-column gap-3 mt-4">
    <v-card
      outlined
      elevation="0"
      class="d-flex flex-column gap-2 pa-3 rounded-lg pt-2"
    >
      <h6 class="mb-0 px-1">Página Inicial</h6>
      <setting-card
        v-model="formData.settings.headerPartyHighlight"
        icon="mdi-party-popper"
        title="Mostrar eventos próximos no banner"
        description="Mostra os eventos mais próximos no banner no lugar do vídeo. Caso não tenha eventos próximos, o vídeo será exibido."
      />
      <setting-card
        v-model="formData.settings.artistsHome"
        icon="mdi-music-note"
        title="Mostrar artistas na página inicial"
        description="Mostrar o carousel de artistas na página inicial da plataforma."
      />

      <setting-card
        v-model="formData.settings.galleryHome"
        icon="mdi-image"
        title="Mostrar galeria de fotos em destaque na página inicial"
        description="Mostrar a galeria de fotos em destaque na página inicial da plataforma."
      />

      <setting-card
        :value="formData.settings.singleParty !== false"
        icon="mdi-calendar-star"
        title="Evento Único"
        description="Configurar a plataforma para exibir apenas um evento específico."
        disable-click-when-active
        @input="toggleSingleParty"
      >
        <template v-if="formData.settings.singleParty !== false">
          <div class="mt-4 selector-wrapper">
            <div class="party-selector-container">
              <party-selector
                v-model="selectedParty"
                label="Selecione o evento"
                :rules="[(v) => !!v || 'Evento é obrigatório']"
                :disabled="loading"
              />
            </div>

            <v-text-field
              v-model="formData.settings.singleParty.coupon"
              class="mt-2"
              label="Cupom (opcional)"
              hint="Código do cupom a ser aplicado"
              outlined
              dense
            />
          </div>
        </template>
      </setting-card>

      <setting-card
        :value="!!formData.settings.homeHighlight"
        icon="mdi-star-circle"
        title="Card de destaque na página inicial"
        description="Exibir um card para atrair novos eventos para a plataforma, com botão de WhatsApp ou formulário de contato."
        disable-click-when-active
        @input="toggleHomeHighlight"
      >
        <template v-if="formData.settings.homeHighlight">
          <div class="mt-4" @click.stop>
            <v-text-field
              v-model="formData.settings.homeHighlight.title"
              label="Título"
              outlined
              dense
              hide-details="auto"
              class="mb-2"
            />
            <v-textarea
              v-model="formData.settings.homeHighlight.description"
              label="Descrição"
              outlined
              dense
              rows="3"
              auto-grow
              hide-details="auto"
              class="mb-2"
            />
            <v-text-field
              v-model="formData.settings.homeHighlight.buttonText"
              label="Texto do botão"
              outlined
              dense
              hide-details="auto"
              class="mb-2"
            />
            <div class="mb-1">
              <small class="text--secondary">Call to action</small>
            </div>
            <v-switch
              v-model="formData.settings.homeHighlight.showLogo"
              label="Mostrar logo da plataforma"
              hide-details
              class="mt-0 pt-0"
              @click.stop
            />
            <v-switch
              v-model="formData.settings.homeHighlight.showWhatsapp"
              label="Botão WhatsApp"
              hide-details
              class="pt-0"
              @click.stop
            />

            <template v-if="formData.settings.homeHighlight.showWhatsapp">
              <phone-input
                class="mt-2"
                label="WhatsApp de destino"
                :phone="formData.settings.homeHighlight.whatsapp.phone"
                :ddi="formData.settings.homeHighlight.whatsapp.ddi"
                @update:phone="
                  (e) => (formData.settings.homeHighlight.whatsapp.phone = e)
                "
                @update:ddi="
                  (e) => (formData.settings.homeHighlight.whatsapp.ddi = e)
                "
                @valid="(v) => (validHomeHighlightWhatsapp = v)"
                dense
                optional
                outlined
                hide-details="auto"
              />
              <v-textarea
                v-model="formData.settings.homeHighlight.whatsapp.message"
                class="mt-2"
                label="Mensagem pré-preenchida"
                hint="Se o WhatsApp estiver vazio, o telefone da plataforma será usado."
                persistent-hint
                outlined
                dense
                rows="2"
                auto-grow
                hide-details="auto"
              />
            </template>

            <v-switch
              v-model="formData.settings.homeHighlight.showForm"
              label="Formulário de contato"
              hide-details
              class="pt-0"
              @click.stop
            />
            <template v-if="formData.settings.homeHighlight.showForm">
              <div class="mt-2">
                <small class="text--secondary">
                  Campos do formulário. Telefone é sempre obrigatório.
                </small>
                <v-checkbox
                  :input-value="true"
                  disabled
                  dense
                  hide-details
                  class="mt-1"
                  label="Telefone (sempre obrigatório)"
                />
                <v-checkbox
                  v-model="formData.settings.homeHighlight.form.name"
                  dense
                  hide-details
                  class="mt-0"
                  label="Nome"
                />
                <v-checkbox
                  v-model="formData.settings.homeHighlight.form.email"
                  dense
                  hide-details
                  class="mt-0"
                  label="E-mail"
                />
              </div>
            </template>
          </div>
        </template>
      </setting-card>
    </v-card>

    <!-- Página do Evento -->
    <v-card
      outlined
      elevation="0"
      class="d-flex flex-column gap-2 rounded-lg pa-4 pt-2"
    >
      <h6 class="mb-0">Página do Evento</h6>
      <setting-card
        v-model="formData.settings.showFullPartyDescription"
        icon="mdi-text-long"
        title="Mostrar descrição completa do evento"
        description="Sempre mostrar a descrição completa do evento na página do evento."
      />

      <setting-card
        v-model="formData.settings.showHalfTicketPolicy"
        icon="mdi-text"
        title="Mostrar texto legal para meia entrada"
        description="Mostrar o texto legal para meia entrada na página do evento."
      >
        <v-btn
          v-if="formData.settings.showHalfTicketPolicy !== false"
          class="mt-2"
          text
          small
          @click.stop="showHalfTicketPolicyModal = true"
        >
          Ver texto legal
        </v-btn>
        <v-dialog v-model="showHalfTicketPolicyModal" max-width="600">
          <v-card>
            <v-card-title> Benefício de meia entrada </v-card-title>
            <v-card-text>
              <p class="text-body-2 mb-0 text--secondary">
                Todos os lotes de ingressos estão com reserva de 40% destinado
                ao atendimento da
                <a
                  href="https://www.planalto.gov.br/ccivil_03/_ato2011-2014/2013/lei/l12933.htm"
                  target="_blank"
                  class="primary--text"
                >
                  lei 13.933/2013
                  <v-icon x-small color="primary" style="margin-top: -2px">
                    mdi-open-in-new
                  </v-icon>
                </a>
                . Lotes onde não constar a emissão de meia entrada é porque
                houve o esgotamento de ingressos para meia entrada, devendo o
                cliente gerar a sua aquisição quando for disponibilizado novo
                lote. O cliente deverá apresentar documento compatível para
                utilização da meia entrada conforme normas estabelecidas em lei.
              </p>
            </v-card-text>
          </v-card>
        </v-dialog>
      </setting-card>

      <setting-card
        v-model="formData.settings.hideRefundPolicy"
        icon="mdi-eye-off"
        title="Ocultar política de reembolso"
        description="Ocultar a política de reembolso da página do evento."
        color="warning"
      />
    </v-card>

    <!-- Rodapé -->
    <v-card
      outlined
      elevation="0"
      class="d-flex flex-column gap-2 rounded-lg pa-4 pt-2"
    >
      <h6 class="mb-0">Rodapé</h6>
      <setting-card
        :value="formData.settings.proconPhone !== false"
        icon="mdi-phone-classic"
        title="Mostrar telefone de suporte"
        description="Mostrar o telefone do PROCON no rodapé da plataforma."
        disable-click-when-active
        @input="toggleProconPhone"
      >
        <v-text-field
          v-if="formData.settings.proconPhone !== false"
          v-model="formData.settings.proconPhone"
          label="Telefone do PROCON"
          hide-details
          class="mt-4"
          hint="151"
          outlined
          dense
        />
      </setting-card>
    </v-card>

    <!-- Telefones de suporte -->
    <v-card outlined elevation="0" class="h-full rounded-lg pa-4 pb-1 pt-2">
      <h6 class="mb-0">Telefones de suporte</h6>
      <v-row>
        <v-col cols="12" md="4">
          <phone-input
            label="Suporte ao produtor"
            :phone="formData.settings.supportContact"
            @update:phone="(e) => (formData.settings.supportContact = e)"
            @valid="(v) => (validSupportContact = v)"
            dense
            optional
            outlined
            hide-details="auto"
          />
        </v-col>
        <v-col cols="12" md="4">
          <phone-input
            label="SAC"
            :phone="formData.settings.sacContact"
            @update:phone="(e) => (formData.settings.sacContact = e)"
            @valid="(v) => (validSacContact = v)"
            dense
            optional
            outlined
            hide-details="auto"
          />
        </v-col>
        <v-col cols="12" md="4">
          <phone-input
            label="Financeiro"
            :phone="formData.settings.financeContact"
            @update:phone="(e) => (formData.settings.financeContact = e)"
            @valid="(v) => (validFinanceContact = v)"
            dense
            optional
            outlined
            hide-details="auto"
          />
        </v-col>
      </v-row>
    </v-card>
  </div>
</template>

<script>
import PhoneInput from "@/components/global/PhoneInput.vue";
import PartySelector from "@/components/global/selector/PartySelector.vue";
import SettingCard from "@/components/global/SettingCard.vue";

export default {
  name: "SettingsStep",
  components: {
    PhoneInput,
    PartySelector,
    SettingCard,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    oldValue: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    validSupportContact: false,
    validSacContact: false,
    validFinanceContact: false,
    validHomeHighlightWhatsapp: false,
    selectedParty: null,
    showHalfTicketPolicyModal: false,
  }),
  computed: {
    hasChanges() {
      if (
        this.oldValue.settings.hideRefundPolicy !==
        this.formData.settings.hideRefundPolicy
      )
        return true;
      if (
        this.oldValue.settings.artistsHome !==
        this.formData.settings.artistsHome
      )
        return true;
      if (
        this.oldValue.settings.galleryHome !==
        this.formData.settings.galleryHome
      )
        return true;

      if (
        this.oldValue.settings.headerPartyHighlight !==
        this.formData.settings.headerPartyHighlight
      )
        return true;

      const oldHomeHighlight = this.oldValue.settings.homeHighlight || false;
      const newHomeHighlight = this.formData.settings.homeHighlight || false;
      if (JSON.stringify(oldHomeHighlight) !== JSON.stringify(newHomeHighlight))
        return true;

      if (
        this.oldValue.settings.proconPhone !==
        this.formData.settings.proconPhone
      )
        return true;

      if (
        this.oldValue.settings.showHalfTicketPolicy !==
        this.formData.settings.showHalfTicketPolicy
      )
        return true;

      if (
        JSON.stringify(this.oldValue.settings.singleParty) !==
        JSON.stringify(this.formData.settings.singleParty)
      )
        return true;

      if (
        this.oldValue.settings.showFullPartyDescription !==
        this.formData.settings.showFullPartyDescription
      )
        return true;

      return false;
    },
    formData: {
      get() {
        const value = this.value;
        const settings = Object.assign(
          {},
          {
            hideRefundPolicy: false,
            artistsHome: true,
            galleryHome: false,
            proconPhone: false,
            headerPartyHighlight: false,
            homeHighlight: false,
            supportContact: "",
            financeContact: "",
            sacContact: "",
            singleParty: false,
            showHalfTicketPolicy: false,
            showFullPartyDescription: false,
          },
          this.oldValue.settings
        );
        if (
          settings.homeHighlight &&
          typeof settings.homeHighlight === "object"
        ) {
          settings.homeHighlight = this.mergeHomeHighlight(
            settings.homeHighlight
          );
        }
        value.settings = settings;
        return value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
  },
  methods: {
    toggleProconPhone(enabled) {
      this.formData.settings.proconPhone = enabled ? "151" : false;
    },
    mergeHomeHighlight(value = {}) {
      return {
        title: "",
        description: "",
        buttonText: "",
        showForm: true,
        showWhatsapp: true,
        showLogo: true,
        ...value,
        whatsapp: {
          ddi: 55,
          phone: "",
          message: "",
          ...(value.whatsapp || {}),
        },
        form: {
          name: false,
          email: false,
          ...(value.form || {}),
        },
      };
    },
    defaultHomeHighlight() {
      const platformName = this.formData.name || "plataforma";
      return this.mergeHomeHighlight({
        title: `Divulgue seus eventos na ${platformName}`,
        description: `Otimize a gestão e a venda de ingressos. Nossa equipe entra em contato para mostrar como a ${platformName} pode transformar seus eventos.`,
        buttonText: "Quero divulgar meu evento",
        showForm: true,
        showWhatsapp: true,
        showLogo: true,
        whatsapp: {
          ddi: 55,
          phone: this.formData.phone || "",
          message: `Olá, gostaria de criar um evento na ${platformName}`,
        },
        form: {
          name: false,
          email: false,
        },
      });
    },
    toggleHomeHighlight(enabled) {
      if (!enabled) {
        this.formData.settings.homeHighlight = false;
      } else {
        this.formData.settings.homeHighlight = this.defaultHomeHighlight();
      }
    },
    normalizeHomeHighlight(value) {
      if (!value) return false;
      const merged = this.mergeHomeHighlight(value);
      return {
        title: String(merged.title || "").trim(),
        description: String(merged.description || "").trim(),
        buttonText: String(merged.buttonText || "").trim(),
        showForm: !!merged.showForm,
        showWhatsapp: !!merged.showWhatsapp,
        showLogo: !!merged.showLogo,
        whatsapp: {
          ddi: Number(merged.whatsapp.ddi) || 55,
          phone: String(merged.whatsapp.phone || "").trim(),
          message: String(merged.whatsapp.message || "").trim(),
        },
        form: {
          name: !!merged.form.name,
          email: !!merged.form.email,
        },
      };
    },
    toggleSingleParty(enabled) {
      if (!enabled) {
        this.formData.settings.singleParty = false;
        this.selectedParty = null;
        return;
      }

      this.formData.settings.singleParty = {
        partyId: null,
        organizationSlug: "",
        coupon: "",
      };
    },
    getFormData() {
      if (!this.hasChanges) return false;

      // Update singleParty with selected party data when enabled
      if (this.formData.settings.singleParty && this.selectedParty) {
        this.formData.settings.singleParty.partyId = this.selectedParty.id;
        this.formData.settings.singleParty.organizationSlug =
          this.selectedParty.Organization?.slug || "";
      }

      return {
        settings: {
          hideRefundPolicy: !!this.formData.settings.hideRefundPolicy,
          artistsHome: !!this.formData.settings.artistsHome,
          galleryHome: !!this.formData.settings.galleryHome,
          headerPartyHighlight: !!this.formData.settings.headerPartyHighlight,
          homeHighlight: this.normalizeHomeHighlight(
            this.formData.settings.homeHighlight
          ),
          showHalfTicketPolicy: !!this.formData.settings.showHalfTicketPolicy,
          proconPhone: this.formData.settings.proconPhone
            ? this.formData.settings.proconPhone
            : false,
          supportContact: this.validSupportContact
            ? this.formData.settings.supportContact
            : "",
          sacContact: this.validSacContact
            ? this.formData.settings.sacContact
            : "",
          financeContact: this.validFinanceContact
            ? this.formData.settings.financeContact
            : "",
          singleParty:
            this.formData.settings.singleParty !== false && this.selectedParty
              ? {
                  partyId: this.selectedParty.id,
                  organizationSlug: this.selectedParty.Organization?.slug || "",
                  coupon: this.formData.settings.singleParty.coupon || "",
                }
              : false,
          showFullPartyDescription:
            !!this.formData.settings.showFullPartyDescription,
        },
      };
    },
  },
  watch: {
    "formData.settings.homeHighlight.showWhatsapp"(val) {
      const highlight = this.formData.settings.homeHighlight;
      if (!highlight) return;
      const defaults = [
        "Enviar",
        "Quero divulgar meu evento",
      ];
      if (!highlight.buttonText || defaults.includes(highlight.buttonText)) {
        highlight.buttonText =
          val
            ? "Falar no WhatsApp"
            : "Quero divulgar meu evento";
      }
    },
    "formData.settings.singleParty": {
      immediate: true,
      handler(newVal) {
        if (newVal && typeof newVal === "object" && newVal.partyId) {
          // Load party data if singleParty is already configured
          this.$nextTick(async () => {
            try {
              const response = await this.$services.parties.getById(
                newVal.partyId
              );
              if (response) {
                this.selectedParty = response;
              }
            } catch (error) {
              console.error("Error loading party data:", error);
            }
          });
        }
      },
    },
  },
};
</script>

<style scoped>
.party-selector-container {
  position: relative;
  z-index: 10;
}
</style>
